import { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, Shield, AlertTriangle, CheckCircle, XCircle, Loader2, ExternalLink, Clock, Building2, Truck, Phone, MapPin, Users, Hash } from 'lucide-react';

const RISK_ICONS = {
  LOW: <CheckCircle className="w-5 h-5 text-green-600" />,
  HIGH: <XCircle className="w-5 h-5 text-red-600" />,
  UNKNOWN: <AlertTriangle className="w-5 h-5 text-yellow-600" />
};

const RISK_STYLES = {
  LOW: 'bg-green-100 text-green-800 border-green-300',
  HIGH: 'bg-red-100 text-red-800 border-red-300',
  UNKNOWN: 'bg-yellow-100 text-yellow-800 border-yellow-300'
};

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-sm font-medium text-slate-900 break-words">{value}</p>
      </div>
    </div>
  );
}

export default function CreditCheck({ user }) {
  const [searchType, setSearchType] = useState('DOT');
  const [queryValue, setQueryValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    api.get('/credit-check/history')
      .then(res => setHistory(res.data))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, []);

  const handleSearch = async () => {
    if (!queryValue.trim()) {
      toast.error('Enter a DOT or MC number');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.get(`/credit-check/${searchType}/${queryValue.trim()}`);
      setResult(res.data);
      if (!res.data.found) {
        toast.error(res.data.message || 'No records found');
      } else {
        toast.success('Lookup complete');
        // Refresh history
        const histRes = await api.get('/credit-check/history');
        setHistory(histRes.data);
      }
    } catch (err) {
      toast.error('Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const loadFromHistory = (item) => {
    setResult(item.result);
    setSearchType(item.search_type);
    setQueryValue(item.query);
  };

  return (
    <div className="space-y-6" data-testid="credit-check-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Broker / Carrier Credit Check</h1>
        <p className="text-slate-600 mt-1">Look up safety ratings, insurance, and authority status via FMCSA</p>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="w-full sm:w-36">
              <Label className="text-xs text-slate-500 mb-1 block">Search By</Label>
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger data-testid="search-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DOT">DOT Number</SelectItem>
                  <SelectItem value="MC">MC Number</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-xs text-slate-500 mb-1 block">{searchType} Number</Label>
              <Input
                value={queryValue}
                onChange={e => setQueryValue(e.target.value)}
                placeholder={searchType === 'DOT' ? 'e.g. 2462923' : 'e.g. 789456'}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                data-testid="credit-check-input"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleSearch} disabled={loading} className="w-full sm:w-auto" data-testid="credit-check-search-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                {loading ? 'Looking up...' : 'Check'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Results */}
        <div className="lg:col-span-2 space-y-4">
          {result && result.found ? (
            <>
              {/* Risk Badge */}
              <div className={`flex items-center gap-3 p-4 rounded-lg border ${RISK_STYLES[result.risk_level] || RISK_STYLES.UNKNOWN}`}>
                {RISK_ICONS[result.risk_level] || RISK_ICONS.UNKNOWN}
                <div>
                  <p className="font-bold text-lg">{result.legal_name || 'Unknown Company'}</p>
                  <p className="text-sm">
                    Status: <strong>{result.operating_status || 'Unknown'}</strong> | Risk: <strong>{result.risk_level}</strong>
                  </p>
                </div>
              </div>

              {/* Details Card */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Company Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <InfoRow icon={Building2} label="Legal Name" value={result.legal_name} />
                  <InfoRow icon={Building2} label="DBA Name" value={result.dba_name} />
                  <InfoRow icon={Shield} label="Entity Type" value={result.entity_type} />
                  <InfoRow icon={Hash} label="USDOT Number" value={result.dot_number} />
                  <InfoRow icon={Hash} label="MC/MX Number" value={result.mc_number} />
                  <InfoRow icon={Hash} label="DUNS Number" value={result.duns_number} />
                  <InfoRow icon={MapPin} label="Physical Address" value={result.physical_address} />
                  <InfoRow icon={MapPin} label="Mailing Address" value={result.mailing_address} />
                  <InfoRow icon={Phone} label="Phone" value={result.phone} />
                </CardContent>
              </Card>

              {/* Operations Card */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Truck className="w-5 h-5 text-blue-600" />
                    Operations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <InfoRow icon={Shield} label="Operating Status" value={result.operating_status} />
                  <InfoRow icon={AlertTriangle} label="Out of Service Date" value={result.out_of_service_date} />
                  <InfoRow icon={Truck} label="Power Units" value={result.power_units} />
                  <InfoRow icon={Users} label="Drivers" value={result.drivers} />
                  <InfoRow icon={Truck} label="Operation Classification" value={result.operation_classification} />
                  <InfoRow icon={Truck} label="Carrier Operation" value={result.carrier_operation} />
                  <InfoRow icon={Truck} label="Cargo Carried" value={result.cargo_carried} />
                  <InfoRow icon={Clock} label="MCS-150 Form Date" value={result.mcs150_date} />
                  <InfoRow icon={Hash} label="MCS-150 Mileage" value={result.mcs150_mileage} />
                </CardContent>
              </Card>

              {/* Links */}
              <div className="flex flex-wrap gap-3">
                {result.public_url && (
                  <a href={result.public_url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                     data-testid="fmcsa-public-link">
                    <ExternalLink className="w-4 h-4" /> View on FMCSA
                  </a>
                )}
                {result.insurance_link && (
                  <a href={result.insurance_link} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                    <Shield className="w-4 h-4" /> Insurance Details
                  </a>
                )}
                {result.authority_link && (
                  <a href={result.authority_link} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">
                    <Shield className="w-4 h-4" /> Authority History
                  </a>
                )}
              </div>
            </>
          ) : result && !result.found ? (
            <Card>
              <CardContent className="py-12 text-center">
                <XCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
                <p className="text-lg font-medium text-slate-700">{result.message}</p>
                <p className="text-sm text-slate-500 mt-1">Try a different DOT or MC number</p>
                {result.public_url && (
                  <a href={result.public_url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-2 mt-4 text-blue-600 hover:underline text-sm">
                    <ExternalLink className="w-4 h-4" /> Search on FMCSA directly
                  </a>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-lg font-medium text-slate-500">Enter a DOT or MC number to check</p>
                <p className="text-sm text-slate-400 mt-1">Get safety ratings, insurance, and authority status</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* History Sidebar */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-500" />
              Recent Lookups
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No lookups yet</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.map(item => (
                  <div
                    key={item.id}
                    onClick={() => loadFromHistory(item)}
                    className="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                    data-testid={`history-${item.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{item.result?.legal_name || item.query}</p>
                      {item.result?.risk_level && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          item.result.risk_level === 'LOW' ? 'bg-green-100 text-green-700' :
                          item.result.risk_level === 'HIGH' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>{item.result.risk_level}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">{item.search_type}: {item.query}</p>
                    <p className="text-xs text-slate-400">{new Date(item.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
